import React from "react";
import { useSearchParams } from "react-router-dom";
import {
  DeskInventoryFilters,
  DeskInventoryFiltersGroupedByType,
} from "../../../domain";
import { useLocationInventoriesQuery } from "../../../hooks";
import { useFloors } from "./useFloors";
import { useLocations } from "./useLocations";

const makeId = (type: string, name: string, quantity: number) =>
  `${type}::${name}::${quantity}`;

type Result = {
  selectedIds: string[];
  filters: DeskInventoryFilters[];
};

export function useSelectedInventories(): Result {
  const { locations } = useLocations();
  const { floors } = useFloors();
  const [searchParams] = useSearchParams();

  const locationId = searchParams.get("location");
  const location = locations.find((it) => it.id === locationId);

  const floorId = searchParams.get("floor");
  const floor = floors.find((it) => it.id === floorId);

  const inventories: DeskInventoryFiltersGroupedByType[] | undefined =
    useLocationInventoriesQuery(
      location ? location.id : locations[0].id,
      floor ? floor.id : undefined
    ).data;
  const [sp] = useSearchParams();
  const raw = sp.get("inventory");

  const selectedFromUrl = React.useMemo(
    () =>
      raw
        ? raw
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    [raw]
  );

  const validIdSet = React.useMemo(() => {
    const set = new Set<string>();
    if (!inventories?.length) return set;
    for (const group of inventories) {
      const type = group.type ?? "Other";
      for (const f of group.filters ?? []) {
        set.add(makeId(type, f.name, f.quantity));
      }
    }
    return set;
  }, [inventories]);

  const selectedIds = React.useMemo(
    () => selectedFromUrl.filter((id) => validIdSet.has(id)),
    [selectedFromUrl, validIdSet]
  );

  const filters = React.useMemo(() => {
    if (!inventories?.length || !selectedIds.length) {
      return [];
    }

    const result: DeskInventoryFilters[] = [];

    for (const group of inventories) {
      const type = group.type ?? "Ostalo";
      for (const f of group.filters ?? []) {
        const id = makeId(type, f.name, f.quantity);
        if (selectedIds.includes(id)) {
          result.push(f);
        }
      }
    }

    return result;
  }, [inventories, selectedIds]);

  return { selectedIds, filters };
}
